import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Star, Filter } from 'lucide-react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { cn } from '@/lib/utils'

interface ActivityItem {
  id: string
  type: 'chat_started' | 'chat_ended' | 'agent_joined' | 'callback_requested' | 'feedback_received' | 'call_completed' | 'call_started' | 'call_missed' | 'voicemail_received' | 'booking_completed'
  message: string
  timestamp: Date | string
  agent?: {
    name: string
    avatar?: string
  }
  metadata?: {
    rating?: number
    duration?: number
  }
}

interface ActivityFeedProps {
  activities: ActivityItem[]
  loading?: boolean
  maxHeight?: string
}

type FilterType = 'all' | 'chats' | 'calls' | 'agents' | 'bookings' | 'callbacks' | 'feedback'

/* Type → editorial timeline dot color. We replaced the per-row tinted-card
   treatment (cards-inside-cards reads as AI-template chrome) with a
   vertical-rule-and-dot timeline. The dot is the only color signal. */
const getActivityDotColor = (type: ActivityItem['type']) => {
  switch (type) {
    case 'chat_started':       return 'bg-brand-periwinkle'
    case 'chat_ended':         return 'bg-muted-foreground/40'
    case 'agent_joined':       return 'bg-emerald-500'
    case 'callback_requested': return 'bg-amber-500'
    case 'call_completed':     return 'bg-emerald-500'
    case 'call_started':       return 'bg-brand-periwinkle'
    case 'call_missed':        return 'bg-rose-500'
    case 'voicemail_received': return 'bg-orange-500'
    case 'feedback_received':  return 'bg-amber-500'
    case 'booking_completed':  return 'bg-violet-500'
    default:                   return 'bg-muted-foreground/40'
  }
}

/* Subtle glow ring for the most-recent (top) row only — draws the eye to
   "what just happened" without making every dot scream for attention. */
const getActivityGlow = (type: ActivityItem['type']) => {
  switch (type) {
    case 'agent_joined':
    case 'call_completed':     return 'shadow-[0_0_0_4px_hsl(var(--background)),0_0_12px_2px_rgb(16_185_129_/_0.45)]'
    case 'call_missed':        return 'shadow-[0_0_0_4px_hsl(var(--background)),0_0_12px_2px_rgb(244_63_94_/_0.45)]'
    case 'booking_completed':  return 'shadow-[0_0_0_4px_hsl(var(--background)),0_0_12px_2px_rgb(139_92_246_/_0.45)]'
    case 'callback_requested':
    case 'feedback_received':  return 'shadow-[0_0_0_4px_hsl(var(--background)),0_0_12px_2px_rgb(245_158_11_/_0.45)]'
    default:                   return 'shadow-[0_0_0_4px_hsl(var(--background)),0_0_12px_2px_hsl(var(--brand-periwinkle)_/_0.45)]'
  }
}

const filterConfig: { value: FilterType; label: string; types: ActivityItem['type'][] }[] = [
  { value: 'all', label: 'All', types: [] },
  { value: 'chats', label: 'Chats', types: ['chat_started', 'chat_ended'] },
  { value: 'calls', label: 'Calls', types: ['call_completed', 'call_started', 'call_missed', 'voicemail_received'] },
  { value: 'agents', label: 'Agents', types: ['agent_joined'] },
  { value: 'bookings', label: 'Bookings', types: ['booking_completed'] },
  { value: 'callbacks', label: 'Callbacks', types: ['callback_requested'] },
  { value: 'feedback', label: 'Feedback', types: ['feedback_received'] }
]

export default function ActivityFeed({
  activities,
  loading = false,
  maxHeight = '400px'
}: ActivityFeedProps) {
  const [filter, setFilter] = useState<FilterType>('all')
  const [showFilters, setShowFilters] = useState(false)

  const filteredActivities = filter === 'all'
    ? activities
    : activities.filter(activity => {
        const config = filterConfig.find(f => f.value === filter)
        return config?.types.includes(activity.type)
      })

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent Activity</CardTitle>
          <CardDescription>Live updates from your chat system</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="h-8 w-8 rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 bg-muted rounded" />
                  <div className="h-3 w-1/2 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Recent Activity</CardTitle>
            <CardDescription>Live updates from calls, chats, and bookings</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'text-xs',
              showFilters && 'bg-accent'
            )}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-3 w-3 mr-1" />
            Filter
          </Button>
        </div>

        {/* Filter pills */}
        {showFilters && (
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t">
            {filterConfig.map((f) => (
              <Button
                key={f.value}
                variant={filter === f.value ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs px-2.5"
                onClick={() => setFilter(f.value)}
              >
                {f.label}
                {f.value !== 'all' && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'ml-1.5 h-4 px-1 text-[10px]',
                      filter === f.value && 'bg-white/20'
                    )}
                  >
                    {activities.filter(a => f.types.includes(a.type)).length}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1 pt-2">
        <ScrollArea className="pr-4" style={{ height: maxHeight }}>
          {filteredActivities.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              {filter === 'all' ? 'No recent activity' : `No ${filter} activity`}
            </div>
          ) : (
            /* Editorial timeline: vertical rule + dots. Replaces the
               cards-inside-cards approach that read as AI-template chrome. */
            <div className="relative pl-1">
              {filteredActivities.map((activity, index) => {
                const isLast = index === filteredActivities.length - 1
                const isFirst = index === 0
                const dotColor = getActivityDotColor(activity.type)
                return (
                  <div
                    key={activity.id}
                    className={cn(
                      'relative flex gap-4 pb-5 last:pb-1',
                      'animate-in fade-in-50 slide-in-from-left-2',
                    )}
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animationFillMode: 'backwards',
                    }}
                  >
                    {/* Vertical rule connecting events — drawn from the dot
                        downward, stops on the last item. */}
                    {!isLast && (
                      <div
                        aria-hidden="true"
                        className="absolute left-[7px] top-3 bottom-0 w-px bg-border/60"
                      />
                    )}
                    {/* The dot. First (most-recent) row gets a soft glow so
                        "what just happened" naturally pulls the eye. */}
                    <div
                      className={cn(
                        'relative z-10 mt-1 h-3.5 w-3.5 rounded-full ring-[3px] ring-card flex-shrink-0',
                        dotColor,
                        isFirst && getActivityGlow(activity.type),
                      )}
                    />
                    <div className="flex-1 min-w-0 -mt-0.5">
                      <p className="text-sm font-medium leading-snug text-foreground">
                        {activity.message}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                        <span className="tabular-nums">
                          {formatDistanceToNow(
                            typeof activity.timestamp === 'string'
                              ? parseISO(activity.timestamp)
                              : activity.timestamp,
                            { addSuffix: true }
                          )}
                        </span>
                        {activity.agent && (
                          <>
                            <span className="text-muted-foreground/40">·</span>
                            <span className="text-foreground/70">{activity.agent.name}</span>
                          </>
                        )}
                        {activity.metadata?.duration && (
                          <>
                            <span className="text-muted-foreground/40">·</span>
                            <span className="tabular-nums">
                              {Math.floor(activity.metadata.duration / 60)}m {activity.metadata.duration % 60}s
                            </span>
                          </>
                        )}
                        {activity.metadata?.rating && (
                          <>
                            <span className="text-muted-foreground/40">·</span>
                            <span className="inline-flex items-center gap-0.5">
                              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                              <span className="font-medium text-foreground/80 tabular-nums">{activity.metadata.rating}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
